import { Route, NavigationGuardNext } from 'vue-router';
import { LoadingProgrammatic } from 'buefy';

import store from '@/store';
import container from '@/plugins/services';
import { AuthService } from '@/data/services/auth.service';
import { aclChecker } from '@/data/utils/acl';
import { Headquarter } from '@/data/models/headquarter.model';
import { CrudService } from '@/data/services/crud.service';
import Constants from '@/data/utils/constants';
import { LocalStorageManager } from '@/data/utils/local-storage';
import { UserService } from '@/data/services/user.service';
import { Listitem } from '@/data/models/listitem.model';

const redirectToLogin = (to: Route) => ({
	path: '/login',
	replace: true,
	query: {
		redirect: to.fullPath
	}
});

const aclGuard = (to: Route, next: NavigationGuardNext) => {
	const acl = to.meta.acl;
	const valid = acl ? aclChecker(acl) : true;
	if (valid) next();
	else next('/404');
};

const storeHeadquarter = async (): Promise<Headquarter | undefined> => {
	const hqService = container.get<CrudService<Headquarter>>('CrudService');
	if (!store.user?.hqid) {
		const hqid = LocalStorageManager.get(Constants.STORAGE_HEADQUARTER);
		if (hqid) {
			let index = -1;
			if (store.headquarters) {
				const hqs = store.headquarters ? store.headquarters.map(hq => hq.id) : [];
				index = hqs.indexOf(+hqid);
			} 
			if (index < 0) {
				LocalStorageManager.remove(Constants.STORAGE_HEADQUARTER);
			} else {
				return store.headquarters[index];
			}
		}
		return store.headquarters[0];
	}
	return await hqService.getEntity(Headquarter, 'headquarter', store.user!.hqid);
};

const listsCache = async (): Promise<{ [key: string]: { [key: string]: string } }> => {
	const listItemService = container.get<CrudService<Listitem>>('CrudService');
	const items = await listItemService.getAll(Listitem, 'selistitem');
	const cache: { [key: string]: { [key: string]: string } } = {};
	items.forEach(item => {
		if (!item.selist) return;
		if (!cache[item.selist.slug]) {
			cache[item.selist.slug] = {};
		}
		cache[item.selist.slug][item.itemvalue] = item.itemlabel;
	});
	return cache;
};

const authGuard = async (to: Route, from: Route, next: NavigationGuardNext) => {
	if (to.matched[0].meta.auth) {
		const authService = container.get<AuthService>('AuthService');
		if (!authService.getToken()) {
			next(redirectToLogin(to));
		} else {
			if (!store.user) {
				let loadingVm = null;
				try {
					loadingVm = LoadingProgrammatic.open({});
					const userService = container.get<UserService>('UserService');
					const user = await authService.getLoggedUser();
					store.user = user;
					const hqs = await userService.getHeadquarters(user!.id);
					store.headquarters = hqs;
					const hq = await storeHeadquarter();
					store.headquarter = hq;
					const listItems = await listsCache();
					store.cachelists = listItems;
					aclGuard(to, next);
				} catch (e) {
					next(redirectToLogin(to));
				} finally {
					if (loadingVm) loadingVm.close();
				}
			} else {
				aclGuard(to, next);
			}
		}
	} else {
		aclGuard(to, next);
	}
};

export {
	authGuard
};